// Definice 17 cílových polí importu — jediný zdroj pravdy pro mapování,
// validaci i serializaci.
//
// Pořadí MUSÍ odpovídat `ImportColumnEnum` v backend
// `schema/admin/ticket/mutations.py`, protože payload se posílá jako POZIČNÍ
// pole. 18. pozice (`sourceRow`) se doplňuje až při serializaci.

export type FieldType = 'text' | 'email' | 'date' | 'number' | 'currency'

export type FieldDef = {
    key: string
    type: FieldType
    /** Bez těchto tří backend nezaloží sadu ani cenu. */
    required: boolean
    /**
     * Pevná hodnota pro všechny řádky. U kódu zakázaná — kód musí být per řádek
     * unikátní, jedna hodnota by vyrobila N identických vstupenek.
     */
    allowFixedValue: boolean
    /** Limit dle modelu Ticket. Překročení = chyba řádku, nikdy tichý ořez. */
    maxLength: number | null
    /** Názvy sloupců, které se v praxi objevují v exportech českých systémů. */
    aliases: string[]
}

export const FIELDS: readonly FieldDef[] = [
    {
        key: 'code',
        type: 'text',
        required: true,
        allowFixedValue: false,
        maxLength: 16,
        aliases: [
            'code',
            'kod',
            'Kód',
            'kód',
            'Kod',
            'Číslo vstupenky',
            'Cislo vstupenky',
            'EAN',
            'ean',
            // Ticketportal a spol. exportují kód jako „Barkód“.
            'Barkód',
            'Barkod',
            'barcode',
            // Enigoo: doloženo v release notes pro Produkty→Prodeje
            // a doplňkový prodej (viz real-world/SYSTEMY.md).
            'Čárový kód',
            'Carovy kod',
            'Čárový kód produktu',
            'Carovy kod produktu',
            // Zahraniční systémy (doloženo, viz real-world/SYSTEMY.md):
            'Barcode Number', // Eventbrite
            'Barcode-Nummer', // Eventim.Light
            'code-barres', // Weezevent
            'Ticket Reference', // Tito
            'Secret', // pretix (check-in list)
            'Ticket secret', // pretix (order list)
            'Ticket-Code' // pretix DE
        ]
    },
    {
        key: 'priceId',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: ['priceId', 'price_id', 'cenaId', 'Cena ID', 'idCeny', 'ID ceny']
    },
    {
        key: 'priceName',
        type: 'text',
        required: true,
        allowFixedValue: true,
        maxLength: null,
        aliases: [
            'priceName',
            'price_name',
            'cena',
            'Cena',
            'nazevCeny',
            'Název na webu',
            'Nazev na webu',
            'nom du tarif', // Weezevent
            'Preisstufe' // Eventim.Light
        ]
    },
    {
        key: 'priceNoDph',
        type: 'number',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: [
            'priceNoDph',
            'price_no_dph',
            'cenaBezDph',
            'Cena bez DPH',
            'Cena za vstupenku - základ',
            'Cena za vstupenku - zaklad'
        ]
    },
    {
        key: 'priceDph',
        type: 'number',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: ['priceDph', 'price_dph', 'dph', 'DPH', 'Cena za vstupenku - DPH']
    },
    {
        key: 'priceAmount',
        type: 'number',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: [
            'priceAmount',
            'price_amount',
            'castka',
            'Částka',
            'castka_celkem',
            'Cena za vstupenku',
            'Price', // pretix, Tito
            'prix', // Weezevent
            'Ticketpreis', // Eventim.Light
            'Preis' // pretix DE
        ]
    },
    {
        key: 'currency',
        type: 'currency',
        required: false,
        allowFixedValue: true,
        maxLength: 3,
        aliases: ['currency', 'mena', 'Měna', 'měna', 'Měna objednávky', 'Mena objednavky']
    },
    {
        key: 'ticketsetId',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: ['ticketsetId', 'ticketset_id', 'sadaId', 'Sada ID', 'ID sady']
    },
    {
        key: 'ticketsetName',
        type: 'text',
        required: true,
        allowFixedValue: true,
        maxLength: null,
        aliases: [
            'ticketsetName',
            'ticketset_name',
            'sada',
            'Sada',
            'sadaVstupenek',
            'Jméno sady',
            'Jmeno sady',
            'Název sady',
            'Nazev sady',
            // Anglické exporty (Eventbrite, Tito, pretix a spol.)
            'ticket set',
            'ticket type',
            'Product'
        ]
    },
    {
        key: 'orderId',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: 16,
        aliases: [
            'orderId',
            'order_id',
            'objednavkaId',
            'Objednávka ID',
            'cisloObjednavky',
            'Číslo objednávky',
            'Cislo objednavky',
            'Order code', // pretix
            'Order Reference', // Tito
            'Bestellnummer' // pretix DE
        ]
    },
    {
        key: 'paymentSource',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: ['paymentSource', 'payment_source', 'zdrojPlatby', 'Zdroj platby']
    },
    {
        key: 'paymentType',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: ['paymentType', 'payment_type', 'typPlatby', 'Typ platby']
    },
    {
        key: 'orderDate',
        type: 'date',
        required: false,
        allowFixedValue: true,
        maxLength: null,
        aliases: [
            'orderDate',
            'order_date',
            'datumObjednavky',
            'Datum objednávky',
            'datum',
            'Vytvořeno dne',
            'Vytvoreno dne',
            'Order date', // pretix
            'Order Created Date', // Tito
            'Created Date', // Hi.Events
            'Bestelldatum' // pretix DE
        ]
    },
    {
        key: 'firstName',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: 32,
        aliases: ['firstName', 'jmeno', 'Jméno', 'jméno', 'Jmeno', 'First Name', 'prénom']
    },
    {
        key: 'lastName',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: 32,
        aliases: ['lastName', 'prijmeni', 'Příjmení', 'příjmení', 'Prijmeni', 'Last Name', 'nom']
    },
    {
        key: 'phone',
        type: 'text',
        required: false,
        allowFixedValue: true,
        maxLength: 16,
        aliases: ['phone', 'telefon', 'Telefon', 'tel', 'Phone Number', 'Ticket Phone Number']
    },
    {
        key: 'email',
        type: 'email',
        required: false,
        allowFixedValue: true,
        maxLength: 128,
        aliases: ['email', 'Email', 'E-mail', 'e-mail', 'Ticket Email', 'Order Email']
    }
] as const

/** Klíče v pozičním pořadí, jak je čte backend. */
export const FIELD_KEYS: readonly string[] = FIELDS.map(f => f.key)

export const REQUIRED_FIELD_KEYS: readonly string[] = FIELDS.filter(f => f.required).map(f => f.key)

export function fieldByKey(key: string): FieldDef | undefined {
    return FIELDS.find(f => f.key === key)
}

/** Tvrdý strop; stejná hodnota je vynucená i na backendu. */
export const MAX_IMPORT_ROWS = 10000
